/**
 * Niveau-gegatete logger.
 *
 * Doel: voorkomen dat debug-output (incl. request-bodies met tokens/PII)
 * in productie naar de console van elke gebruiker lekt.
 *
 * Gedrag:
 * - In development (`NODE_ENV !== 'production'`): alle niveaus actief.
 * - In productie: `debug`/`info`/`log` zijn no-ops; `warn`/`error` blijven
 *   actief (waardevol voor support en foutrapportage).
 * - Runtime-override: zet `localStorage['notizy:debug'] = '1'` om in productie
 *   alsnog alle niveaus aan te zetten (handig om mee te kijken op één toestel).
 *   Terug naar normaal: verwijder de sleutel of zet op '0'.
 */

type LogLevel = 'debug' | 'info' | 'log' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
    debug: 10,
    info: 20,
    log: 20,
    warn: 30,
    error: 40,
};

const isProduction = process.env.NODE_ENV === 'production';

/**
 * Minimaal log-niveau dat daadwerkelijk naar de console gaat.
 * Wordt per call geëvalueerd zodat de runtime-override direct effect heeft.
 */
function minLevel(): number {
    if (!isProduction) return LEVEL_ORDER.debug;
    try {
        const flag = window.localStorage.getItem('notizy:debug');
        if (flag === '1' || flag === 'true') return LEVEL_ORDER.debug;
    } catch {
        // localStorage kan ontbreken (SSR/privacy-modus) — val terug op prod-default.
    }
    return LEVEL_ORDER.warn;
}

function emit(level: LogLevel, args: unknown[]): void {
    if (LEVEL_ORDER[level] < minLevel()) return;
    // `log` mapt op console.log; overige niveaus op de gelijknamige methode.
    const method = level === 'log' ? 'log' : level;
    // eslint-disable-next-line no-console
    (console[method] as (...a: unknown[]) => void)(...args);
}

export const logger = {
    debug: (...args: unknown[]) => emit('debug', args),
    info: (...args: unknown[]) => emit('info', args),
    log: (...args: unknown[]) => emit('log', args),
    warn: (...args: unknown[]) => emit('warn', args),
    error: (...args: unknown[]) => emit('error', args),
};

export default logger;
